import React, { useState } from "react";
import styled from "styled-components";

import {
  BackgroundContainer,
  Card,
  MainColumn as Column,
  ElementContainer,
  FlexibleContainer,
  NavBarContainer,
  RowContainer,
} from "../../components/containers/styledComponents";
import {
  CompassMainHeader,
  CompassParagraph,
} from "../../components/common/styledComponents";

import { MESSAGES } from "../../messages";
import { greyText } from "../../styles";
import { SideMenu } from "../../components/common/SideMenu";
import { NavBar } from "../../components/common/NavBar";
import {
  StyledTable,
  TableContainer,
} from "../../components/tables/styledComponents";
import { CellType } from "../../models/tableModels";
import { CustomTableCell } from "../../components/tables/CustomTableCell";
import { getColumnTypeFromKeyOrValue } from "./utils";
import editIcon from "../../assets/svg/edit.svg";
import plusIcon from "../../assets/svg/plus.svg";
import { PageButton } from "../../components/visuals/PageButton";
import { useWorkspace } from "../../hooks/useWorkspace";
import { NoteModal } from "./NoteModal";
import { TaskColumnType } from "../../models/workflowModels";
import { TaskModal } from "./TaskModal";

const WorkspaceTableSection = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  width: 100%;
  margin-left: 10px;

  /* Card */
  & > div {
    flex: 1;
    display: flex;
    flex-direction: column;
    min-height: 0;
    height: auto !important;
  }

  /* Cascade flex through Ant Design table DOM */
  ${TableContainer} {
    flex: 1;
    display: flex;
    flex-direction: column;
    min-height: 0;
  }

  .ant-table-wrapper,
  .ant-spin-nested-loading,
  .ant-spin-container,
  .ant-table,
  .ant-table-container {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
  }

  .ant-table-body {
    flex: 1;
    max-height: none !important;
    overflow-y: auto !important;
  }
`;

export const Workspace = (): JSX.Element => {
  const [show, setShow] = useState<boolean>(false);

  const {
    handleEdit,
    columns,
    isLoading,
    dataSource,
    columnTypes,
    displayNoteModal,
    onHideModal,
    onHideTaskModal,
    handleAddNote: onNoteAdd,
    handleAddTask: onTaskAdd,
    handleUpdateTask: onTaskUpdate,
    displayTaskModal,
    handleDisplayTaskModal,
  } = useWorkspace();

  const getButtonContent = (text: string): JSX.Element => (
    <CompassParagraph fontSize={14} cursor="pointer">
      {text}
    </CompassParagraph>
  );

  return (
    <BackgroundContainer>
      <Column flex={show ? "0 0 15%" : "0 0 0%"} show={show}>
        <SideMenu show={show} setShow={setShow} />
      </Column>
      <Column flex="1" bg="#F5F5F5" overflow={false}>
        <NavBarContainer>
          <RowContainer mt={1} mb={1}>
            <NavBar address={`my-workspace`} />
          </RowContainer>
          <RowContainer ai={"flex-start"} jc={"flex-start"} mt={-15}>
            <CompassMainHeader pt={1} ml={12}>
              My Workspace
            </CompassMainHeader>
          </RowContainer>
          <RowContainer ai={"flex-start"} jc={"flex-start"} mt={1}>
            <CompassParagraph ml={20} color={greyText}>
              {MESSAGES.WORKSPACE.DESCRIPTION}
            </CompassParagraph>
          </RowContainer>
        </NavBarContainer>
        <RowContainer ml={10} mt={1} jc="flex-end">
          <ElementContainer>
            <PageButton
              content={getButtonContent("Edit")}
              image={editIcon}
              action={handleEdit}
              isLoading={isLoading}
              he={true}
            />
            <FlexibleContainer mr={"20px"}>
              <PageButton
                content={getButtonContent("Add task")}
                image={plusIcon}
                action={handleDisplayTaskModal}
                isLoading={isLoading}
                he={true}
              />
            </FlexibleContainer>
          </ElementContainer>
        </RowContainer>
        <WorkspaceTableSection>
          <Card w={100} h={"auto"} m="0 30px 0 10px" isLoading={isLoading}>
            <CompassParagraph fontSize={16} bold={true}>
              Active Workflows
            </CompassParagraph>
            <TableContainer p="10px">
              <StyledTable
                dataSource={dataSource}
                isLoading={isLoading}
                hasEveryCellBorderBottom={true}
                scroll={{ y: 1, x: "max-content" }}
              >
                {columns.map((column: TaskColumnType) => (
                  <StyledTable.Column
                    {...column}
                    key={column.key}
                    render={(value: CellType): JSX.Element => (
                      <CustomTableCell
                        value={value}
                        type={
                          getColumnTypeFromKeyOrValue(
                            column.key as string,
                            value,
                            columnTypes,
                            isLoading,
                          ).type
                        }
                      ></CustomTableCell>
                    )}
                  />
                ))}
              </StyledTable>
            </TableContainer>
          </Card>
        </WorkspaceTableSection>
      </Column>
      <NoteModal
        shouldDisplay={displayNoteModal}
        onHide={onHideModal}
        onNoteAdd={onNoteAdd}
      />
      <TaskModal
        onHide={onHideTaskModal}
        shouldDisplay={displayTaskModal}
        onTaskAdd={onTaskAdd}
        onTaskUpdate={onTaskUpdate}
      />
    </BackgroundContainer>
  );
};
